/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-param-reassign */
import { Layer } from "@iventis/mapbox-gl";
import { lineDistanceMarkerSpriteName } from "../constants";

type StylePropertyToScale = "line-width" | "text-size" | "icon-size" | "text-halo-width" | "symbol-spacing" | "icon-text-fit-padding" | "icon-translate" | "text-translate";

/** Modifies lines and symbols to show better in exports */
export function layerScaleModifier(mapLayers: Layer[], scale: number, layerIdsToSkipTextModifier: string[] = []) {
    mapLayers.forEach((layer) => {
        // For lines increase their width
        if (layer.type === "line") {
            scaleModifier(layer, scale, layer.paint?.["line-width"], "line-width");
        }

        // For symbols increase their size, text size and text-halo size
        if (layer.type === "symbol" && !layerIdsToSkipTextModifier.includes(layer.id)) {
            scaleModifier(layer, scale, layer.layout?.["text-size"], "text-size");

            const iconFitsText = layer.layout?.["icon-text-fit"] != null && layer.layout["icon-text-fit"] !== "none";
            if (iconFitsText) {
                // If the icon fits the text, we need to scale the padding instead of the icon size
                // Otherwise it doesn't match how it appears in the main map
                scaleModifier(layer, scale, layer.layout?.["icon-text-fit-padding"], "icon-text-fit-padding");
            } else {
                // If it doesn't fit the text, just scale the size
                scaleModifier(layer, scale, layer.layout?.["icon-size"], "icon-size");
            }

            scaleModifier(layer, scale, layer.paint?.["text-halo-width"], "text-halo-width");
            scaleModifier(layer, scale, layer.layout?.["symbol-spacing"] ?? 250, "symbol-spacing");

            // We only want to scale these for the distance markers
            if (layer.layout?.["icon-image"] === lineDistanceMarkerSpriteName) {
                scaleModifier(layer, scale, layer.paint?.["icon-translate"], "icon-translate");
                scaleModifier(layer, scale, layer.paint?.["text-translate"], "text-translate");
            }
        }
    });
}

/** Selects correct function for layer style property value */
function scaleModifier(layer: Layer, scale: number, value: number | object | any[], propertyChanged: StylePropertyToScale) {
    if (typeof value === "number") {
        numberValueScaleModifier(layer, scale, value, propertyChanged);
    } else if (Array.isArray(value)) {
        if (propertyChanged === "icon-text-fit-padding" && value.every((arrayValue) => typeof arrayValue === "number")) {
            layer.layout["icon-text-fit-padding"] = value.map((paddingValue) => {
                if (typeof paddingValue === "number") {
                    return paddingValue * scale;
                }
                return paddingValue;
            });
            return;
        }

        if (propertyChanged === "icon-translate" && value.every((arrayValue) => typeof arrayValue === "number")) {
            layer.paint["icon-translate"] = value.map((translateValue) => {
                if (typeof translateValue === "number") {
                    return translateValue * scale;
                }
                return translateValue;
            });
            return;
        }

        if (propertyChanged === "text-translate" && value.every((arrayValue) => typeof arrayValue === "number")) {
            layer.paint["text-translate"] = value.map((translateValue) => {
                if (typeof translateValue === "number") {
                    return translateValue * scale;
                }
                return translateValue;
            });
            return;
        }

        scaleArrayValue(value, scale);
    } else if (typeof value === "object") {
        scaleObjectValue(value, scale, propertyChanged);
    }
}

function numberValueScaleModifier(layer: Layer, scale: number, value: number, propertyChanged: StylePropertyToScale) {
    switch (propertyChanged) {
        case "line-width":
            layer.paint["line-width"] = value * scale;
            break;
        case "text-size":
            layer.layout["text-size"] = value * scale;
            break;
        case "icon-size":
            layer.layout["icon-size"] = value * scale;
            break;
        case "text-halo-width":
            layer.paint["text-halo-width"] = value * scale;
            break;
        case "symbol-spacing":
            layer.layout["symbol-spacing"] = scaleSymbolSpacing(value, scale);
            break;
        default:
            throw new Error(`${propertyChanged} is not handled`);
    }
}

/** Scales a style property which is an array */
function scaleArrayValue(value: any[], scale: number) {
    value.forEach((nestedValue, index) => {
        // Literal expressions wrap array values so Mapbox treats them as values rather than nested expressions.
        if (Array.isArray(nestedValue) && nestedValue[0] === "literal" && Array.isArray(nestedValue[1])) {
            nestedValue[1] = nestedValue[1].map((literalValue) => (typeof literalValue === "number" ? literalValue * scale : literalValue));
        }

        // Handle step expression
        if (typeof nestedValue === "number" && index % 2 === 0) {
            value[index] = nestedValue * scale;
        }

        // Handle match expressions
        if (Array.isArray(nestedValue) && nestedValue[0] === "match") {
            nestedValue.forEach((matchExpressionValue, matchIndex) => {
                // Last value is default value
                if (matchIndex === nestedValue.length - 1 && typeof matchExpressionValue === "number") {
                    value[index][matchIndex] = matchExpressionValue * scale;
                }
                // Every number which is an odd index
                if (typeof matchExpressionValue === "number" && matchIndex % 2 === 1) {
                    value[index][matchIndex] = matchExpressionValue * scale;
                }
            });
        }

        // Handle case expressions
        if (Array.isArray(nestedValue) && nestedValue[0] === "case") {
            nestedValue.forEach((caseExpressionValue, caseIndex) => {
                // Last value is default value
                if (caseIndex === nestedValue.length - 1 && typeof caseExpressionValue === "number") {
                    value[index][caseIndex] = caseExpressionValue * scale;
                }

                // Every number which is an even index
                if (typeof caseExpressionValue === "number" && caseIndex % 2 === 0) {
                    value[index][caseIndex] = caseExpressionValue * scale;
                }
            });
        }

        // Handle step expressions
        if (Array.isArray(nestedValue) && nestedValue[0] === "step") {
            nestedValue.forEach((stepExpressionValue, stepIndex) => {
                // Third value in array is default value
                if (stepIndex === 2 && typeof stepExpressionValue === "number") {
                    value[index][stepIndex] = stepExpressionValue * scale;
                }

                // Every even number which is after the default value
                if (stepIndex > 2 && stepIndex % 2 === 0 && typeof stepExpressionValue === "number") {
                    value[index][stepIndex] = stepExpressionValue * scale;
                }
            });
        }
    });
}

/** Scales a style property value which is an object */
function scaleObjectValue(value: any, scale: number, propertyChanged: StylePropertyToScale) {
    value.stops.forEach((stop) => {
        if (propertyChanged === "symbol-spacing") {
            // Keep to max for symbol spacing
            stop[1] = scaleSymbolSpacing(stop[1], scale);
            return;
        }
        stop[1] *= scale;
    });
}

/** Mapbox requires symbol spacing to be at least one pixel, or it will throw an error */
function scaleSymbolSpacing(value: number, scale: number) {
    return Math.max(value * scale, 1);
}
